/**
 * A variant's (or a colour combo's) colours grouped by the part they sit on:
 * frame and lens on eyewear, «رنگ» (body) on an accessory. Parts come in the
 * backend's part order; a part without colours is left out.
 */
export type ColorPartGroup<C> = { slug: string; title: string; colors: C[] };

type WithColorParts<C> = {
	color_parts?: ColorPartGroup<C>[] | null;
	parts?: ColorPartGroup<C>[] | null;
	frame_colors?: C[] | null;
	lens_colors?: C[] | null;
};

/** Older payloads (cached pages, a cart saved before the change) only carry frame and lens. */
export function colorPartsOf<C>(item: WithColorParts<C> | null | undefined): ColorPartGroup<C>[] {
	if (!item) return [];
	const groups = item.color_parts ?? item.parts;
	if (Array.isArray(groups)) return groups.filter(group => group.colors?.length);
	return [
		{ slug: "frame", title: "فریم", colors: item.frame_colors ?? [] },
		{ slug: "lens", title: "لنز", colors: item.lens_colors ?? [] },
	].filter(group => group.colors.length);
}

/** Every colour of the groups, in part order. */
export function allPartColors<C>(groups: ColorPartGroup<C>[]): C[] {
	return groups.flatMap(group => group.colors);
}

/** «فریم مشکی، لنز دودی»; a part titled «رنگ» reads as «رنگ قرمز». */
export function colorPartsText<C extends { title: string }>(groups: ColorPartGroup<C>[], separator = " ، "): string {
	return groups.map(group => group.title + " " + group.colors.map(color => color.title).join(separator)).join(" ");
}
